import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

type FilterState = Record<string, any> & { count: number; active: number };

type FilterTagStore = {
  filter: FilterState;
  readonly addFilter: (newFilter: Record<string, any>) => void;
  readonly replaceFilter: (newFilter: Record<string, any>) => void;
  readonly applyFilter: () => void;
  readonly resetFilter: () => void;
};

function filterCount(f: Record<string, any>): number {
  let count = 0;

  for (const prop in f) {
    if (prop === 'count' || prop === 'active') continue;
    const fil = f[prop];

    if (Array.isArray(fil)) {
      count += fil.length;
    } else if (fil) {
      count += 1;
    }
  }

  return count;
}

function activeFilterCount(f: Record<string, any>): number {
  let active = 0;

  for (const prop in f) {
    if (prop === 'count' || prop === 'active') continue;
    const fil = f[prop];

    if (Array.isArray(fil)) {
      fil.forEach((item: any) => {
        active += item && item.applied ? 1 : 0;
      });
    } else {
      active += fil && fil.applied ? 1 : 0;
    }
  }

  return active;
}

function withCounts(filter: Record<string, any>): FilterState {
  return {
    ...filter,
    count: filterCount(filter),
    active: activeFilterCount(filter)
  };
}

const INITIAL_STATE: FilterState = { count: 0, active: 0 };

export const filterTagStore = create<FilterTagStore>(set => ({
  filter: INITIAL_STATE,

  addFilter: newFilter =>
    set(state => {
      const merged = { ...state.filter, ...newFilter };
      delete merged.modified;
      return { filter: withCounts(merged) };
    }),

  replaceFilter: newFilter =>
    set(() => {
      const replaced = { ...newFilter };
      delete replaced.modified;
      return { filter: withCounts(replaced) };
    }),

  applyFilter: () =>
    set(state => {
      const applied: Record<string, any> = {};

      for (const prop in state.filter) {
        if (prop === 'count' || prop === 'active' || prop === 'modified') continue;
        const fil = state.filter[prop];

        if (Array.isArray(fil)) {
          applied[prop] = fil.map((item: any) => ({ ...item, applied: true }));
        } else {
          applied[prop] = { ...fil, applied: true };
        }
      }

      return { filter: { ...applied, count: filterCount(applied), active: filterCount(applied) } };
    }),

  resetFilter: () => set({ filter: INITIAL_STATE })
}));

export const useFilterTagStore = <T = FilterTagStore>(selector: (state: FilterTagStore) => T = state => state as T) =>
  filterTagStore(useShallow(selector));
